
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowRight, Sparkles, Zap, 
  Play, CheckCircle2, 
  Instagram, Globe, Cpu, Ghost, Layers, Fingerprint
} from 'lucide-react';
import { SEO } from '../../components/ui/SEO';
import { Logo } from '../../components/ui/Logo';

const SaaSHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [currency, setCurrency] = useState<'BRL' | 'USD'>('USD');

  useEffect(() => {
    const handleScroll = () => setScrolled((window as any).scrollY > 20);
    (window as any).addEventListener('scroll', handleScroll);
    
    // Auto-detect currency
    const lang = navigator.language || (navigator as any).userLanguage;
    setCurrency(lang.includes('pt') ? 'BRL' : 'USD');

    return () => (window as any).removeEventListener('scroll', handleScroll);
  }, []);

  const prices = {
      starter: currency === 'BRL' ? 'R$ 97' : '$29',
      pro: currency === 'BRL' ? 'R$ 297' : '$99'
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-brand-green selection:text-black overflow-x-hidden">
      <SEO 
        title="HeroFrog Studio | AI Video Suite"
        description="The ultimate video production OS for creators. Script, Edit and Scale with DeepSeek AI."
      />
      
      {/* NAVBAR */}
      <nav className={`fixed top-6 left-0 right-0 z-50 transition-all duration-500 flex justify-center px-4`}>
        <div className={`
            flex items-center justify-between px-6 py-4 rounded-full transition-all duration-300 w-full max-w-5xl
            ${scrolled ? 'bg-black/80 backdrop-blur-xl border border-white/10 shadow-2xl scale-100' : 'bg-transparent border-transparent scale-105'}
        `}>
          <Link to="/" className="flex items-center gap-3 group">
            <Logo size="sm" className="group-hover:rotate-12 transition-transform duration-500" />
            <span className="text-lg font-black tracking-tighter uppercase italic text-white hidden md:block">
                HeroFrog<span className="text-brand-green">.</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
             {!user && (
                 <Link to="/login" className="hidden md:block text-xs font-bold uppercase tracking-widest text-white hover:text-brand-green transition-colors">
                    Login
                 </Link>
             )}
             <button 
                onClick={() => navigate(user ? '/admin/dashboard' : '/login')} 
                className="bg-white hover:bg-brand-green hover:scale-105 text-black px-6 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_#00E676]"
             >
                {user ? 'Studio' : (currency === 'BRL' ? 'Começar' : 'Start Now')} <ArrowRight size={12} />
             </button>
          </div>
        </div>
      </nav>

      <main className="relative">
        
        {/* HERO SECTION */}
        <section className="relative pt-48 pb-32 overflow-hidden min-h-screen flex flex-col items-center justify-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(0,230,118,0.15)_0%,_transparent_50%)] pointer-events-none"></div>
            
            <div className="container mx-auto px-6 relative z-10 text-center flex flex-col items-center">
                
                <div className="inline-flex items-center gap-2 bg-[#111] border border-white/10 px-4 py-2 rounded-full mb-12 animate-fade-in-up hover:border-brand-green/50 transition-colors group cursor-default">
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green"></span>
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">
                        DeepSeek R1 Engine Active
                    </span>
                </div>
                
                <h1 className="text-6xl md:text-8xl lg:text-[9rem] font-black uppercase italic tracking-tighter leading-[0.85] mb-10 max-w-7xl mx-auto mix-blend-screen">
                    <span className="block text-white">Create Faster.</span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-white">Scale Harder.</span>
                </h1>
                
                <p className="text-gray-400 text-lg md:text-xl max-w-2xl font-medium leading-relaxed mb-14">
                    {currency === 'BRL' 
                        ? 'O Editor de Vídeo com IA que entende seu nicho. Roteirize, edite e viralize sem tocar na timeline.'
                        : 'The AI Video Editor that understands your niche. Script, edit, and viralize without touching the timeline.'}
                </p>

                <div className="flex flex-col sm:flex-row gap-6 w-full max-w-md mx-auto mb-24">
                    <button onClick={() => navigate('/login')} className="group relative flex-1 bg-white text-black py-5 rounded-2xl font-black uppercase text-xs tracking-widest overflow-hidden hover:scale-[1.02] transition-transform shadow-[0_10px_40px_rgba(255,255,255,0.2)]">
                        <span className="relative z-10 flex items-center justify-center gap-3">
                            {currency === 'BRL' ? 'Entrar no Studio' : 'Enter Studio'} <Sparkles size={16} className="text-brand-green fill-brand-green" />
                        </span>
                    </button>
                </div>
            </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="py-32 bg-[#080808] border-t border-white/5">
            <div className="container mx-auto px-6 text-center">
                <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter mb-16">
                    {currency === 'BRL' ? 'Planos de Produção' : 'Production Plans'}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {/* Starter Tier */}
                    <div className="bg-[#0F0F0F] border border-white/10 rounded-[3rem] p-12 flex flex-col items-center">
                        <h3 className="text-xl font-bold uppercase tracking-widest text-gray-400 mb-4">Starter</h3>
                        <div className="text-5xl font-black italic mb-2">{prices.starter}<span className="text-lg text-gray-500">/mo</span></div>
                        <ul className="space-y-4 mb-10 text-sm font-medium text-gray-300 mt-8">
                            <li className="flex items-center gap-2"><CheckCircle2 size={16}/> Jirinu AI Editor (Basic)</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={16}/> 5 AI Scripts / Week</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={16}/> 1080p Export</li>
                        </ul>
                        <button onClick={() => navigate('/login')} className="w-full bg-white/10 text-white py-4 rounded-xl font-bold uppercase text-xs hover:bg-white hover:text-black transition-all">
                            Start Creating
                        </button>
                    </div>

                    {/* Pro Tier */}
                    <div className="bg-[#0F0F0F] border-2 border-brand-green rounded-[3rem] p-12 flex flex-col items-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 bg-brand-green text-black text-[10px] font-black uppercase px-4 py-2 rounded-bl-xl">Best Value</div>
                        <h3 className="text-xl font-bold uppercase tracking-widest text-brand-green mb-4">Pro Studio</h3>
                        <div className="text-5xl font-black italic mb-2">{prices.pro}<span className="text-lg text-gray-500">/mo</span></div>
                        <ul className="space-y-4 mb-10 text-sm font-medium text-white mt-8">
                            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-green"/> Unlimited AI Projects</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-green"/> DeepSeek R1 Strategy</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-green"/> 4K Export & Cloud Storage</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-green"/> Priority Support</li>
                        </ul>
                        <button onClick={() => navigate('/login')} className="w-full bg-brand-green text-black py-4 rounded-xl font-bold uppercase text-xs hover:scale-105 transition-all shadow-neon">
                            Get Pro Access
                        </button>
                        <p className="mt-4 text-[10px] text-gray-500 uppercase tracking-widest flex items-center gap-2 justify-center">
                            <Zap size={10} /> Secure payment via Stripe
                        </p>
                    </div>
                </div>
            </div>
        </section>
      </main>

      <footer className="py-12 bg-black border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
            <p className="text-[10px] font-mono uppercase text-gray-700">© 2024 HeroFrog Studio.</p>
        </div>
      </footer>
    </div>
  );
};

export default SaaSHomePage;
